import { useEffect, useState } from "react";
import { api, API, useAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Award, CheckCircle2, XCircle, Clock, ChevronRight, ArrowLeft, Video, Star, UserPlus, Copy, X } from "lucide-react";

function InviteDialog({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk-invite", { emails: text });
      setResult(data);
      if (data.created.length) onDone();
      toast.success(`${data.created.length} account(s) created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const copyList = () => {
    const lines = result.created.map((c) => `${c.email}  —  password: ${result.default_password}`).join("\n");
    navigator.clipboard.writeText(lines).then(() => toast.success("Copied to clipboard"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="invite-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><UserPlus size={18} className="text-teal-700" /> Invite students</h2>
          <button data-testid="invite-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        {!result ? (
          <>
            <p className="text-sm text-slate-500">Paste email addresses separated by commas, spaces or new lines. Accounts are created instantly with the temporary password <span className="font-mono font-semibold text-slate-700">Welcome123!</span> — students must change it on first sign-in.</p>
            <textarea data-testid="invite-emails-input" rows={6} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={"asha.patel@example.org\nravi.kumar@example.org, meera@example.org"}
              className="w-full rounded-xl border border-slate-200 p-3.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-600" />
            <button data-testid="invite-submit-btn" onClick={submit} disabled={busy || !text.trim()}
              className="w-full h-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
              {busy ? "Creating accounts…" : "Create accounts"}
            </button>
          </>
        ) : (
          <div className="space-y-3" data-testid="invite-results">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 text-sm text-emerald-800">
              <span className="font-bold">{result.created.length}</span> account(s) created · temporary password: <span className="font-mono font-bold">{result.default_password}</span>
            </div>
            {result.created.length > 0 && (
              <div className="border border-slate-100 rounded-xl divide-y divide-slate-50 max-h-48 overflow-y-auto">
                {result.created.map((c) => (
                  <div key={c.email} className="px-3.5 py-2 text-sm flex items-center justify-between gap-2">
                    <span className="text-slate-700">{c.email}</span>
                    <span className="text-[11px] text-slate-400">{c.name}</span>
                  </div>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && <p className="text-xs text-amber-700">Already registered (skipped): {result.skipped.join(", ")}</p>}
            {result.invalid.length > 0 && <p className="text-xs text-red-600">Invalid entries ignored: {result.invalid.join(", ")}</p>}
            <div className="flex gap-2">
              {result.created.length > 0 && (
                <button data-testid="invite-copy-btn" onClick={copyList} className="flex-1 h-10 rounded-xl border border-teal-700 text-teal-800 font-semibold text-sm inline-flex items-center justify-center gap-1.5 hover:bg-teal-50 transition-colors">
                  <Copy size={14} /> Copy list
                </button>
              )}
              <button data-testid="invite-done-btn" onClick={onClose} className="flex-1 h-10 rounded-xl bg-teal-800 text-white font-semibold text-sm hover:bg-teal-700 transition-colors">Done</button>
            </div>
            <p className="text-[11px] text-slate-400">Share these credentials with your students. Each student will be required to set a personal password the first time they sign in.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const VideoBadge = ({ s }) => {
  const map = { approved: ["bg-emerald-100 text-emerald-800", "Approved"], pending: ["bg-amber-100 text-amber-800", "Pending"], rejected: ["bg-red-100 text-red-700", "Resubmit"], none: ["bg-slate-100 text-slate-400", "—"] };
  const [cls, label] = map[s] || map.none;
  return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
};

function Profile({ uid, onBack }) {
  const [data, setData] = useState(null);
  const { pick } = useLang();
  useEffect(() => { api.get(`/instructor/students/${uid}`).then((r) => setData(r.data)).catch(() => {}); }, [uid]);
  if (!data) return <p className="text-slate-500">Loading…</p>;
  const { user, enrollments, submissions, certificates, applications } = data;
  return (
    <div className="space-y-4" data-testid="student-profile">
      <button data-testid="profile-back-btn" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-teal-700 font-semibold"><ArrowLeft size={15} /> All students</button>
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center gap-4 shadow-sm">
        <span className="w-14 h-14 rounded-2xl bg-teal-800 text-white flex items-center justify-center font-heading text-xl font-extrabold">{user.name?.[0]}</span>
        <div className="flex-1">
          <h1 className="font-heading text-xl font-extrabold text-slate-900">{user.name}</h1>
          <p className="text-sm text-slate-500">{user.email} · joined {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
        <div className="text-center"><p className="font-heading text-2xl font-extrabold text-amber-600">{certificates.length}</p><p className="text-[10px] text-slate-400">Certificates</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 text-sm">Enrollments & Progress</h2>
          {enrollments.length === 0 && <p className="text-sm text-slate-400">No enrollments</p>}
          {enrollments.map((e) => (
            <div key={e.id} className="border border-slate-100 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-slate-800 text-sm">{e.track_id}</p>
                <VideoBadge s={e.video_status} />
              </div>
              <p className="text-xs text-slate-500">Lessons: {e.lessons_completed.length} · Exam: {e.exam_score != null ? `${e.exam_score}%` : "—"} {e.exam_passed && "✓"} · Cert: {e.certificate_id || "—"}</p>
            </div>
          ))}
        </section>
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 text-sm">Practical Submissions</h2>
          {submissions.length === 0 && <p className="text-sm text-slate-400">No submissions</p>}
          {submissions.map((s) => (
            <div key={s.id} className="border border-slate-100 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-slate-800 text-sm flex items-center gap-1.5"><Video size={14} className="text-teal-600" /> {pick(s.track_title)}</p>
                <VideoBadge s={s.status} />
              </div>
              <p className="text-xs text-slate-500">{new Date(s.created_at).toLocaleString()}{s.scores && ` · scores: ${Object.entries(s.scores).map(([k, v]) => `${k} ${v}/5`).join(", ")}`}</p>
              {s.feedback && <p className="text-xs text-slate-500 italic">“{s.feedback}”</p>}
            </div>
          ))}
          {applications.length > 0 && (
            <>
              <h2 className="font-heading font-bold text-slate-900 text-sm pt-2">Hiring</h2>
              {applications.map((a) => (
                <p key={a.id} className="text-xs text-slate-600 border border-slate-100 rounded-xl p-3">{pick(a.track_title)} — <span className="font-semibold capitalize">{a.status}</span>{a.interview_at && ` · interview ${a.interview_at}`}</p>
              ))}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default function Students() {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [invite, setInvite] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { pick } = useLang();
  const { user } = useAuth();
  const canInvite = user && ["admin", "superadmin"].includes(user.role);
  useEffect(() => { api.get("/instructor/students").then((r) => setRows(r.data)).catch(() => setRows([])); }, [reloadKey]);
  if (!rows) return <p className="text-slate-500">Loading…</p>;
  if (selected) return <Profile uid={selected} onBack={() => setSelected(null)} />;
  const shown = rows.filter((r) => (r.name + r.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      {invite && <InviteDialog onClose={() => setInvite(false)} onDone={() => setReloadKey((k) => k + 1)} />}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Students</h1>
          <p className="text-sm text-slate-500">{rows.length} enrollment record(s) across all tracks</p>
        </div>
        <div className="flex items-center gap-2.5">
          {canInvite && (
            <button data-testid="invite-students-btn" onClick={() => setInvite(true)}
              className="h-11 px-4 rounded-xl bg-teal-800 hover:bg-teal-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors">
              <UserPlus size={15} /> Invite students
            </button>
          )}
          <input data-testid="students-search-input" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)}
            className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 w-64" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-[11px] text-slate-400 uppercase tracking-wider border-b border-slate-100">
              <th className="py-3 px-4">Student</th><th className="px-3">Course</th><th className="px-3">Progress</th>
              <th className="px-3">Exam</th><th className="px-3">Practical</th><th className="px-3">Certificate</th><th className="px-3" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No students found</td></tr>}
            {shown.map((r, i) => (
              <tr key={`${r.user_id}-${r.track_id}`} data-testid={`student-row-${i}`} onClick={() => setSelected(r.user_id)}
                className="border-b border-slate-50 hover:bg-teal-50/40 cursor-pointer transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-full bg-teal-800 text-white flex items-center justify-center text-xs font-bold shrink-0">{r.name?.[0]}</span>
                    <div><p className="font-semibold text-slate-800">{r.name}</p><p className="text-[11px] text-slate-400">{r.email}</p></div>
                  </div>
                </td>
                <td className="px-3 text-slate-600">{pick(r.track_title)}</td>
                <td className="px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${r.progress_pct}%` }} /></div>
                    <span className="text-xs text-slate-500">{r.progress_pct}%</span>
                  </div>
                </td>
                <td className="px-3">{r.exam_score != null ? <span className={`font-semibold ${r.exam_passed ? "text-emerald-700" : "text-red-600"}`}>{r.exam_score}%</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3"><VideoBadge s={r.video_status} /></td>
                <td className="px-3">{r.certificate_id ? <span className="flex items-center gap-1 text-amber-700 text-xs font-semibold"><Award size={13} /> {r.certificate_id}</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 text-slate-300"><ChevronRight size={16} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
